import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../common/Button";
import { Input } from "../common/Input";

export function AuthScreen() {
  const {
    authMode,
    tokenInput,
    loginPassword,
    signupPassword,
    twoFACode,
    require2FA,
    requirePin,
    isLoading,
    error,
    signup,
    login,
    dispatch,
    ACTIONS,
  } = useAuth();

  const [localError, setLocalError] = useState("");

  const handleSignup = async () => {
    if (!signupPassword) {
      setLocalError("Password is required");
      return;
    }

    if (signupPassword.length < 8) {
      setLocalError("Password must be at least 8 characters");
      return;
    }

    if (!/[A-Za-z]/.test(signupPassword) || !/\d/.test(signupPassword)) {
      setLocalError("Password must contain at least one letter and one number");
      return;
    }

    setLocalError("");
    const result = await signup(signupPassword);

    if (!result.success) {
      setLocalError(result.error);
    }
  };

const handleLogin = async () => {
  if (!tokenInput || !loginPassword) {
    setLocalError('Session token and password are required');
    return;
  }
  
  setLocalError('');
  console.log('Attempting login with token:', tokenInput.substring(0, 8) + '...');
  const result = await login();
  console.log('Login result:', result);
  
  if (result && result.requirePin) {
    console.log('PIN required, pending data should be stored in context');
    setLocalError('');
  } else if (result && result.require2FA) {
    console.log('2FA code required');
    setLocalError('');
  } else if (result && !result.success && result.error) {
    setLocalError(result.error);
  }
};

  const handleRecoverAccount = () => {
    window.dispatchEvent(new CustomEvent("openRecoverModal"));
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800">Secure Chat</h1>
          <p className="text-gray-600">
            End-to-end encrypted messaging with PFS
          </p>
          <div className="mt-2 bg-yellow-100 p-2 rounded-md text-xs text-yellow-800">
            ⚠️ Prototype Version - Not for Production Use
          </div>
        </div>

        <div className="bg-white shadow-2xl rounded-xl p-8">
          <div className="flex border-b mb-6">
            <button
              onClick={() => {
                dispatch({ type: ACTIONS.SET_AUTH_MODE, payload: "login" });
                dispatch({ type: ACTIONS.SET_REQUIRE_2FA, payload: false });
                dispatch({ type: ACTIONS.SET_REQUIRE_PIN, payload: false });
                dispatch({ type: ACTIONS.SET_TWOFA_CODE, payload: "" });
                setLocalError("");
              }}
              className={`flex-1 py-2 font-semibold ${
                authMode === "login"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500"
              }`}
            >
              Log In
            </button>
            <button
              onClick={() => {
                dispatch({ type: ACTIONS.SET_AUTH_MODE, payload: "signup" });
                dispatch({ type: ACTIONS.SET_REQUIRE_2FA, payload: false });
                dispatch({ type: ACTIONS.SET_REQUIRE_PIN, payload: false });
                setLocalError("");
              }}
              className={`flex-1 py-2 font-semibold ${
                authMode === "signup"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500"
              }`}
            >
              Sign Up
            </button>
          </div>

          {(error || localError) && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error || localError}
            </div>
          )}

          {authMode === "login" ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mb-2">
                <strong>Enter your current session token</strong>
                <br />
                <span className="text-xs">
                  You'll receive a new token after login
                </span>
              </div>

              <Input
                type="text"
                placeholder="Current Session Token"
                value={tokenInput}
                onChange={(e) =>
                  dispatch({
                    type: ACTIONS.SET_TOKEN_INPUT,
                    payload: e.target.value,
                  })
                }
                disabled={isLoading}
              />

              <Input
                type="password"
                placeholder="Password"
                value={loginPassword}
                onChange={(e) =>
                  dispatch({
                    type: ACTIONS.SET_LOGIN_PASSWORD,
                    payload: e.target.value,
                  })
                }
                disabled={isLoading}
              />

              {require2FA && (
                <Input
                  type="text"
                  placeholder="2FA Code (6 digits)"
                  value={twoFACode}
                  onChange={(e) =>
                    dispatch({
                      type: ACTIONS.SET_TWOFA_CODE,
                      payload: e.target.value.replace(/\D/g, "").slice(0, 6),
                    })
                  }
                  maxLength="6"
                  className="text-center text-2xl tracking-widest"
                  autoFocus
                />
              )}

              {requirePin && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700 mb-2">
                  <strong>🔐 PIN Required</strong>
                  <br />
                  <span className="text-xs">
                    Please enter your 6-digit PIN in the popup window to
                    complete login
                  </span>
                </div>
              )}

              <Button
                onClick={handleLogin}
                disabled={isLoading}
                variant="success"
                fullWidth
              >
                {isLoading
                  ? "Logging In..."
                  : require2FA
                    ? "Verify 2FA"
                    : "Log In"}
              </Button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <Button
                onClick={handleRecoverAccount}
                variant="warning"
                fullWidth
              >
                Recover Account
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Input
                type="password"
                placeholder="Password (min 8 chars, letters & numbers)"
                value={signupPassword}
                onChange={(e) =>
                  dispatch({
                    type: ACTIONS.SET_SIGNUP_PASSWORD,
                    payload: e.target.value,
                  })
                }
                disabled={isLoading}
              />
              <p className="text-xs text-gray-500">
                Password must be at least 8 characters with at least one letter
                and one number
              </p>
              <Button
                onClick={handleSignup}
                disabled={isLoading}
                variant="primary"
                fullWidth
              >
                {isLoading ? "Creating Account..." : "Sign Up"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
